import React from 'react';
import TankRefillControls from './Nutrition/TankRefillControls';
import { supportsTankRefill } from '../Utils/nutrition_utils';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { FormHelperText, MenuItem, Typography } from '@material-ui/core';
import { Select } from '../FormWidgets/Select';
import { ShowSetting } from '../FormWidgets/ShowSliderSetting';
import { SettingGroupGrid, ValueBox } from '../FormWidgets/SettingGroup';
import { nutritionScope, tankRoutes, TANK_LOW_LIMITS } from '../Utils/nutrition_utils';
import { formatTankAvailability, isTankEnabled } from '../Utils/tank_availability';

export function TankLowSetting({
  controller,
  id,
  field,
  minutes = 0,
  controllerMinutes = 0,
  controls,
  disabled
}) {
  return (
    <ShowSetting
      controller={controller}
      id={id}
      show={`tank_${field}`}
      cur_value={minutes}
      controller_value={controllerMinutes}
      min={0}
      max={TANK_LOW_LIMITS[field]}
      unit="min"
      disabled={disabled}
      controls={controls}
    />
  );
}

export default function NutritionTanks({ controller, parent, values, controls }) {
  const { t } = useTranslation();
  const snapshot = useSnapshot(parent);
  const controllerSnapshot = useSnapshot(controller);
  const nutrition = snapshot.settings.nutrition;
  const info = snapshot.settings.irrigation_tank_info ?? [];
  const scope = nutritionScope(controller, parent);
  const tanks = new Map((nutrition.tanks ?? []).map((tank) => [tank.tank_id, tank]));
  info.forEach((tank) => {
    if (!tanks.has(tank.tank_id)) tanks.set(tank.tank_id, null);
  });

  return (
    <>
      {tanks.size === 0 && <Typography>{t('nutrition_no_tanks')}</Typography>}
      {[...tanks].map(([id, tank]) => {
        const live = info.find((item) => item.tank_id === id);
        const owner = live?.owner_zone;
        const knownOwner = owner === 254 || (Number.isInteger(owner) && owner >= 0 && owner < 9);
        const owned = knownOwner && owner === scope && Boolean(tank);
        const editable = owned && controllerSnapshot.access_rights?.nutrition?.write;
        const draft = values.tanks[id];
        const routes = tank && isTankEnabled(live) ? tankRoutes(tank, nutrition.routes) : [];
        const selected = draft?.selected_route_id ?? tank?.selected_route_id ?? 0;
        const routeUnavailable =
          selected !== 0 && !routes.some((route) => route.route_id === selected);
        const name = live?.name || t('tank_id', { id });
        const availability = formatTankAvailability(controllerSnapshot, snapshot, id, t);
        const ownerPath =
          owner === 254
            ? `/controller/${controller.id}/nutrition`
            : `/controller/${controller.id}/zones/zone_${owner}/nutrition`;
        return (
          <SettingGroupGrid
            key={id}
            controller={controller}
            id={`nutrition_tank_${id}`}
            label={name}
            subtitle={t('tank_available_to', { availability })}
          >
            {owned ? (
              <>
                <ValueBox controller={controller} id="tank_filling_route">
                  <Typography gutterBottom id={`tank-${id}-route-label`}>
                    {t('tank_filling_route')}
                  </Typography>
                  <Select
                    fullWidth
                    id={`tank-${id}-route`}
                    labelId={`tank-${id}-route-label`}
                    value={selected}
                    controller_value={tank.selected_route_id}
                    disabled={!editable || (routes.length === 0 && selected === 0)}
                    error={routeUnavailable}
                    onChange={(event) =>
                      controls.setValue(`tanks.${id}.selected_route_id`, Number(event.target.value))
                    }
                  >
                    <MenuItem value={0}>{t('tank_filling_disabled')}</MenuItem>
                    {routes.map((route) => (
                      <MenuItem key={route.route_id} value={route.route_id}>
                        {route.name} ({route.route_id})
                      </MenuItem>
                    ))}
                    {routeUnavailable && (
                      <MenuItem value={selected} disabled>
                        {(selected <= 31 &&
                          nutrition.routes?.find((route) => route.route_id === selected)?.name) ||
                          t('unknown')}{' '}
                        ({selected})
                      </MenuItem>
                    )}
                  </Select>
                  {(routeUnavailable || tank.setup_type !== 1) && (
                    <FormHelperText error={routeUnavailable}>
                      {t(routeUnavailable ? 'tank_route_unavailable' : 'tank_filling_unsupported')}
                    </FormHelperText>
                  )}
                </ValueBox>
                {supportsTankRefill(controllerSnapshot, tank) && (
                  <TankRefillControls
                    controller={controller}
                    parent={parent}
                    tank={tank}
                    draft={draft ?? tank}
                    controls={controls}
                    disabled={!editable}
                  />
                )}
                {Object.keys(TANK_LOW_LIMITS).map((field) => (
                  <TankLowSetting
                    key={field}
                    controller={controller}
                    id={`tanks.${id}.${field}`}
                    field={field}
                    minutes={draft?.[field] ?? 0}
                    controllerMinutes={tank[field] ?? 0}
                    controls={controls}
                    disabled={!editable}
                  />
                ))}
              </>
            ) : (
              <ValueBox controller={controller} id="tank_settings">
                {knownOwner && owner !== scope ? (
                  <Typography component={Link} to={ownerPath} color="primary">
                    {t('tank_open_settings')}
                  </Typography>
                ) : (
                  <Typography>
                    {Number.isInteger(owner) && !knownOwner
                      ? `${t('unknown')} (${owner})`
                      : t('tank_settings_unavailable')}
                  </Typography>
                )}
              </ValueBox>
            )}
          </SettingGroupGrid>
        );
      })}
    </>
  );
}
